import { createStore } from 'zustand/vanilla'
import { readJson, writeJson } from '../services/storage'
import {
  isLiveryId,
  isLiveryIdFor,
  type LiveryId,
  type LiveryVehicle,
} from '../components/Map/liveries'

// User-facing display settings for how live transit vehicles are rendered on the
// 3D map. Persisted to localStorage, mirroring favorites.ts / device.ts:
// @capacitor/preferences is NOT a dependency, so we use localStorage, which inside a
// Capacitor WebView is the app-private sandbox (each app has its own isolated origin).
// Swapping to Preferences later only requires changing the load/persist helpers.

const SETTINGS_KEY = 'transit_settings'

/**
 * How a vehicle is drawn — one exclusive choice, not a pile of toggles:
 *  • `labels`    — always the route bullet/badge, never a mesh. The cheapest mode.
 *  • `trains`    — always the 3D model, at every zoom the map allows.
 *  • `both`      — `trains` with the bullet kept on top of the model instead of being
 *                  the thing that replaces it. The mesh says which way and how long,
 *                  the bullet says which line, and only the mesh modes ever lose that
 *                  last one: a silver R160 looks the same on every route it runs.
 *  • `optimized` — the mesh where it reads and the bullet where it doesn't, which is
 *                  the zoom ladder in r160LodForZoom. The default, because it is the
 *                  only mode that is neither wasteful zoomed out nor bare zoomed in.
 */
export type VehicleDisplay = 'labels' | 'trains' | 'both' | 'optimized'

const VEHICLE_DISPLAYS: VehicleDisplay[] = ['labels', 'trains', 'both', 'optimized']

/**
 * Which basemap style the map wears, or what it follows to choose one:
 *  • `device`   — the OS light/dark setting. The default.
 *  • `schedule` — the subway rather than the phone: dark through late-night service,
 *                 light the rest of the day.
 *  • `light` / `dark` — pinned.
 *
 * Only the MAP is themed. The app chrome stays one fixed palette, because the
 * ui-themes catppuccin build ships no `prefers-color-scheme` variant to switch to.
 * Resolution (and the late-night window) lives in `components/Map/mapTheme.ts`;
 * this is only the stored preference.
 */
export type MapThemePref = 'device' | 'light' | 'dark' | 'schedule'

const MAP_THEME_PREFS: MapThemePref[] = ['device', 'light', 'dark', 'schedule']

export function isMapThemePref(v: unknown): v is MapThemePref {
  return (MAP_THEME_PREFS as unknown[]).includes(v)
}

interface PersistedSettings {
  /** Which of the four render modes above the map is in. */
  vehicleDisplay: VehicleDisplay
  /** What the basemap's light/dark style follows. */
  mapTheme: MapThemePref
  /**
   * The paint each fleet wears, applied to that body's unwrapped sidewall panel.
   * `default` is as-built and is the only value that costs nothing at all — see
   * components/Map/liveries.ts. A livery is artwork on the panel, NOT a tint over the
   * whole model, so it composes with every render mode instead of having to exclude one.
   *
   * ⚠️ **Two settings, because the fleets do not share a paint history.** This was one
   * `livery` key applied to both, which forced every theme to have a half for each body —
   * and that is how the trains ended up wearing a "classic" scheme that has never existed
   * outside this app (see `LIVERIES.classic.subway`). Split, each picker offers only what
   * its own fleet can actually wear, and a rider can run today's buses beside as-built
   * trains without either choice being a lie.
   *
   * The Minion skin used to be a `minionTheme` boolean beside this, and had to exclude
   * `labels` because a whole-model repaint needs a whole model to repaint. It is a livery
   * now, so that rule is gone rather than merely relaxed.
   */
  subwayLivery: LiveryId
  busLivery: LiveryId
}

const DEFAULTS: PersistedSettings = {
  vehicleDisplay: 'optimized',
  mapTheme: 'device',
  subwayLivery: 'default',
  // ⚠️ The two fleets default differently, and it is the same fact that split the setting in
  // the first place. As-built is what an R160 looks like — unpainted stainless, on the
  // platform, today — so the trains open on it and cost nothing. A bus has no as-built: **an
  // MTA bus IS its livery**, and the exporter's blockout white is a scheme no Xcelsior in the
  // city wears, so opening on it showed every rider a vehicle that does not exist. `mta` is
  // the bus drawn from the reference photographs, which is what a rider looking at a corridor
  // of buses should see before touching a single setting. It is not free — the bus fleet now
  // takes the patched-glTF path by default — and that is the price of being right.
  // `AS_BUILT_OFFERED` takes the row out of the bus picker to match.
  busLivery: 'mta',
}

function loadSettings(): PersistedSettings {
  return readJson(SETTINGS_KEY, { ...DEFAULTS }, decodeSettings)
}

function decodeSettings(value: unknown): PersistedSettings | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const obj = value as Record<string, unknown>
  // Migration: the mode used to be a `showLineLabels` boolean whose false meant
  // "3D models with the zoom ladder" — i.e. today's `optimized`. Anyone who had it
  // on kept asking for labels, so honour that; everyone else lands on the default.
  const legacy: VehicleDisplay | null =
    obj.showLineLabels === true ? 'labels' : obj.showLineLabels === false ? 'optimized' : null
  // Migration: the Minion skin was its own boolean, so anyone who had it on is carried
  // over to the livery that replaced it. A livery they chose explicitly wins — the two
  // settings coexisted for a while, and someone sitting on `minionTheme` plus an ad wrap
  // picked the wrap after the skin and should keep it.
  const legacyLivery: unknown =
    isLiveryId(obj.livery) && obj.livery !== 'default'
      ? obj.livery
      : obj.minionTheme === true
        ? 'minion'
        : obj.livery
  // Migration: `livery` was ONE id dressing both fleets. Whoever had one keeps it on
  // every body that can wear it, and lands on that fleet's own default for any body that
  // cannot — which today is the trains of anyone sitting on `classic`, a scheme the subway
  // fleet never wore. Falling back rather than substituting a near-miss is deliberate:
  // as-built is what an R160 looks like, so nothing is invented on their behalf.
  //
  // Migration: as-built LEFT the bus picker, so `isLiveryIdFor('bus', 'default')` is now
  // false and everyone stored on it — which is everyone who never opened the sheet, since
  // it used to be the default — drops through to `DEFAULTS.busLivery`, i.e. `mta`. That is
  // the whole migration; it needs no case of its own precisely because the guard is per
  // fleet. A bus livery they chose explicitly is still an offered id and still wins.
  const liveryFor = (vehicle: LiveryVehicle, stored: unknown): LiveryId =>
    isLiveryIdFor(vehicle, stored)
      ? stored
      : isLiveryIdFor(vehicle, legacyLivery)
        ? legacyLivery
        : DEFAULTS[vehicle === 'subway' ? 'subwayLivery' : 'busLivery']
  return {
    vehicleDisplay: VEHICLE_DISPLAYS.includes(obj.vehicleDisplay as VehicleDisplay)
      ? (obj.vehicleDisplay as VehicleDisplay)
      : (legacy ?? DEFAULTS.vehicleDisplay),
    mapTheme: isMapThemePref(obj.mapTheme) ? obj.mapTheme : DEFAULTS.mapTheme,
    subwayLivery: liveryFor('subway', obj.subwayLivery),
    busLivery: liveryFor('bus', obj.busLivery),
  }
}

function persistSettings(s: PersistedSettings): void {
  writeJson(SETTINGS_KEY, s)
}

interface SettingsState extends PersistedSettings {
  setVehicleDisplay: (v: VehicleDisplay) => void
  setMapTheme: (v: MapThemePref) => void
  /**
   * One setter keyed by vehicle rather than two, so the sheet can draw its two pickers
   * from one loop and a third body would need no new store surface.
   */
  setLivery: (vehicle: LiveryVehicle, v: LiveryId) => void
}

// Three independent settings, and they stayed independent only once the Minion skin
// stopped being a whole-model repaint. A repaint needs a mesh, so it had to exclude
// `labels` and that exclusion had to be enforced here; a livery paints ONE material —
// the body's own sidewall panel — so it cannot collide with a render mode at all. In
// `labels` there is simply no mesh for it to land on, which needs no rule to express.
export const useSettingsStore = createStore<SettingsState>((set) => ({
  ...loadSettings(),
  setVehicleDisplay: (v) => set({ vehicleDisplay: v }),
  // Themes the basemap under the vehicles rather than the vehicles.
  setMapTheme: (v) => set({ mapTheme: v }),
  setLivery: (vehicle, v) =>
    set(vehicle === 'subway' ? { subwayLivery: v } : { busLivery: v }),
}))

useSettingsStore.subscribe((state, prev) => {
  if (
    state.vehicleDisplay !== prev.vehicleDisplay ||
    state.mapTheme !== prev.mapTheme ||
    state.subwayLivery !== prev.subwayLivery ||
    state.busLivery !== prev.busLivery
  ) {
    persistSettings({
      vehicleDisplay: state.vehicleDisplay,
      mapTheme: state.mapTheme,
      subwayLivery: state.subwayLivery,
      busLivery: state.busLivery,
    })
  }
})
